import { Injectable, signal } from '@angular/core';
import { IDealerByLocation } from '@core/interface';
import { formatHourDefault } from '@core/utils';
import { DealerSpecificServices } from '../../enums/dealer-specific-services.enum';
import { StorageKeys } from '@core/enums';
import { VehicleService } from '../vehicle/vehicle.service';

@Injectable({
  providedIn: 'root',
})
export class DealerHelperService {
  private readonly vehicleService = new VehicleService();

  list = signal<{
    dealers: IDealerByLocation[];
    loading: boolean;
    error: boolean
  }>({dealers: [], loading: false, error: false});

  preferredDealer = signal<{
    dealer: IDealerByLocation | null;
    loading: boolean;
    error: boolean
  }>({dealer: null, loading: false, error: false});

  // Save Dealer ID to storage
  saveDealerId(dealerId: string): void {
    sessionStorage.setItem(StorageKeys.DEALER_ID, dealerId || '');
  }

  // Retrieve Dealer ID from storage
  getDealerId(): string {
    return sessionStorage.getItem(StorageKeys.DEALER_ID) ?? '';
  }

  // Remove Dealer ID from storage
  clearDealerId(): void {
    sessionStorage.removeItem(StorageKeys.DEALER_ID);
  }

  // Check if Dealer ID exists
  hasDealerId(): boolean {
    return !!this.getDealerId();
  }

  /**
   * Filtra os serviços do dealer por modalidade e os armazena em listas separadas.
   *
   * Esta função filtra os serviços disponíveis do dealer em três listas distintas
   * com base nas modalidades de serviço: retirada e entrega (pickupDelivery),
   * serviço móvel (mobileService) e serviço de guincho (winchService).
   * As listas resultantes são armazenadas nas propriedades correspondentes da classe.
   *
   * @param {IDealerByLocation} dealer - O dealer cujos serviços serão filtrados
   * @returns {IDealerByLocation} - O dealer atualizado com as listas de serviços filtrados.
   */
  mapServiceModalityLists(dealer: IDealerByLocation): IDealerByLocation {
    dealer.pdlModalityList = dealer.services.filter(service => service.pickupDelivery);
    dealer.winchModalityList = dealer.services.filter(service => service.winchService);
    dealer.msvModalityList = dealer.services.filter(service => service.mobileService);

    return dealer;
  }

  /**
   * Formata os horários de funcionamento do dealer.
   *
   * Esta função processa os horários de serviço do dealer e define os horários
   * de abertura e fechamento para dias de semana e fins de semana, além de
   * determinar se o dealer funciona aos sábados e domingos.
   *
   * @param {IDealerByLocation} dealer - O dealer cujos horários serão formatados
   * @returns {IDealerByLocation} - O dealer atualizado com os horários formatados
   */
  formatDealerServiceHours(dealer: IDealerByLocation): IDealerByLocation {
    dealer.highestTimeWeek = '';
    dealer.lowestTimeWeek = '';
    dealer.highestTimeWeekend = '';
    dealer.lowestTimeWeekend = '';
    dealer.lowestDay = 0;
    dealer.openSaturday = false;
    dealer.openSunday = false;

    if (dealer && dealer.serviceHours !== null && dealer.serviceHours) {
      for (let index = 0; index <= dealer.serviceHours.length; index++) {
        if (dealer.serviceHours[index]) {
          if (
            dealer.serviceHours[index].open &&
            dealer.serviceHours[index].open !== '-' &&
            dealer.serviceHours[index].close !== '-'
          ) {
            if (dealer.serviceHours[index].days[0] <= 5) {
              // define horario de abertura na semana
              if (dealer.lowestTimeWeek === '') {
                dealer.lowestTimeWeek = formatHourDefault(
                  dealer.serviceHours[index].open
                );
                dealer.lowestDay = index;
              }
              // define horario de fechamento semana
              if (dealer.serviceHours[index].days[0] >= dealer.lowestDay) {
                dealer.highestTimeWeek = formatHourDefault(
                  dealer.serviceHours[index].close
                );
              }
            } else {
              // regra fim de semana
              if (dealer.serviceHours[index].days[0] === 6) {
                dealer.openSaturday = true;
                dealer.lowestTimeWeekend = formatHourDefault(
                  dealer.serviceHours[index].open
                );
                dealer.highestTimeWeekend = formatHourDefault(
                  dealer.serviceHours[index].close
                );
              }
              if (dealer.serviceHours[index].days[0] === 7) {
                dealer.openSunday = true;
                if (dealer.lowestTimeWeekend === '') {
                  dealer.lowestTimeWeekend = formatHourDefault(
                    dealer.serviceHours[index].open
                  );
                }
                dealer.highestTimeWeekend = formatHourDefault(
                  dealer.serviceHours[index].close
                );
              }
            }
          }
        }
      }
    }

    return dealer;
  }

  /**
   * Verifica se um dealer possui um serviço específico de localizador de concessionárias.
   *
   * @param {IDealerByLocation | null} dealer - O dealer a ser verificado
   * @param {string} serviceName - O nome do serviço a ser verificado
   * @returns {boolean} - True se o dealer possui o serviço, false caso contrário
   */
  checkDealerHasLocatorService(dealer: IDealerByLocation | null, serviceName: string): boolean {
    if (!dealer) return false;
    const services: string[] = dealer.dealerLocatorServices || [];
    const serviceToFind = serviceName.toString().toLowerCase();
    return services.some((service) => service.toString().toLowerCase().includes(serviceToFind));
  }

  /**
   * Constrói e formata um dealer com todas as transformações necessárias.
   *
   * Esta função aplica todas as formatações necessárias ao dealer:
   * - Formatação de horários de atendimento
   * - Mapeamento das listas de modalidades de serviço
   *
   * @param {IDealerByLocation} dealer - O dealer a ser processado
   * @returns {IDealerByLocation} - O dealer completamente formatado
   */
  buildFormattedDealer(dealer: IDealerByLocation): IDealerByLocation {
    return {
      ...dealer,
      ...this.formatDealerServiceHours(dealer),
      ...this.mapServiceModalityLists(dealer),
    };
  }

  /**
   * Checks if the currently selected dealer offers Mustang-specific services.
   *
   * Searches through the dealer's `dealerLocatorServices` array for services
   * containing "Mustang" (case-insensitive).
   *
   * @returns `true` if the dealer supports Mustang services, `false` if no dealer is selected or dealer doesn't support Mustang
   *
   * @example
   * ```typescript
   * if (service.dealerHasMustang() && service.isMustangVehicle()) {
   *   // Allow access to Mustang-specific scheduling experience
   * }
   * ```
   */
  dealerHasMustangService(services: string[]): boolean {
    return services.some((s) => (s ?? '').toString().toLowerCase().includes(DealerSpecificServices.mustang.toLowerCase()));
  }

  /**
   * Checks if the currently selected dealer offers electric vehicle services.
   *
   * Searches through the dealer's `dealerLocatorServices` array for services
   * containing "Electrical" (case-insensitive).
   *
   * @returns `true` if the dealer supports electrical vehicle services, `false` if no dealer is selected or dealer doesn't support electrical services
   *
   * @example
   * ```typescript
   * if (service.dealerHasElectricalService() && service.isElectricalVehicle()) {
   *   // Allow access to electrical vehicle scheduling experience
   * }
   * ```
   */
  protected dealerHasElectricalService(dealer: IDealerByLocation): boolean {
    const arr: string[] = dealer.dealerLocatorServices ?? [];
    return arr.some((s) => (s ?? '').toString().toLowerCase().includes(DealerSpecificServices.electric.toLowerCase()));
  }

  /**
   * Define as flags de especialista de serviço para o dealer com base no tipo de veículo.
   *
   * Esta função verifica se o veículo é Mustang ou elétrico e marca as propriedades
   * `serviceSpecialist` e `isUnavailable` do dealer de acordo com a disponibilidade
   * do serviço especializado.
   *
   * @param {IDealerByLocation} dealer - O dealer a ser processado
   * @param {boolean} isMustangVehicle - Indica se o veículo é Mustang
   * @param {boolean} isElectricVehicle - Indica se o veículo é elétrico
   * @returns {IDealerByLocation} - O dealer com as flags atualizadas
   */
  setDealerSpecialistFlags(
    dealer: IDealerByLocation
  ): IDealerByLocation {
    // Verificar se o veículo é Mustang e marcar o serviceSpecialist apropriado
    if (this.vehicleService.isMustangVehicle()) {
      const hasMustangService = this.dealerHasMustangService(dealer.dealerLocatorServices || []);
      dealer.serviceSpecialist = DealerSpecificServices.mustang;
      dealer.unSelectable = !hasMustangService;
    }

    // Verificar se o veículo é elétrico e marcar o serviceSpecialist apropriado
    if (this.vehicleService.isElectricalVehicle()) {
      const hasElectricService = this.dealerHasElectricalService(dealer);
      dealer.serviceSpecialist = DealerSpecificServices.electric;
      dealer.unSelectable = !hasElectricService;
    }

    return dealer;
  }
}
